// Which of Today's sections are open. Every section starts collapsed, so a
// long day is a short page of headings with counts; opening one is remembered
// on this device, and "Expand all" / "Collapse all" flip the lot.

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'cerebro.today.sections';

/** `mode` is what a section is unless it is listed in `except`. */
interface Saved {
  mode: 'collapsed' | 'expanded';
  except: string[];
}

const DEFAULT: Saved = { mode: 'collapsed', except: [] };

function read(): Saved {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (
      typeof parsed === 'object' && parsed !== null &&
      ((parsed as Saved).mode === 'collapsed' || (parsed as Saved).mode === 'expanded') &&
      Array.isArray((parsed as Saved).except) && (parsed as Saved).except.every((k) => typeof k === 'string')
    ) {
      return parsed as Saved;
    }
  } catch {
    // Storage that cannot be read is the same as nothing saved.
  }
  return DEFAULT;
}

let current: Saved | null = null;
const listeners = new Set<() => void>();

const snapshot = (): Saved => (current ??= read());

function save(next: Saved) {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Without storage the choice lasts for this visit.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Forget the cached choice, so the next read comes from storage (a new visit, or a test). */
export function resetTodaySections() {
  current = null;
}

export function useTodaySections() {
  const saved = useSyncExternalStore(subscribe, snapshot);
  return {
    allExpanded: saved.mode === 'expanded' && saved.except.length === 0,
    isOpen: (key: string) => (saved.mode === 'expanded') !== saved.except.includes(key),
    toggle: (key: string) =>
      save({ mode: saved.mode, except: saved.except.includes(key) ? saved.except.filter((k) => k !== key) : [...saved.except, key] }),
    setAll: (open: boolean) => save({ mode: open ? 'expanded' : 'collapsed', except: [] }),
  };
}
